import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiCheck, FiPlus, FiTrash2, FiUpload, FiEye } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { STORAGE_URL } from '../../api/axios';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const layoutIds = ['modern', 'classic', 'minimal', 'elegant', 'food', 'advanced'] as const;
const fonts = ['Inter', 'Poppins', 'Roboto', 'Open Sans', 'Montserrat', 'Playfair Display', 'Lato', 'Cairo', 'Tajawal'];

export default function DesignSettings() {
  const { store, reloadStore, lang } = useOutletContext<{ store: any; reloadStore: () => void; lang: DashLang }>();
  const [form, setForm] = useState<any>({});
  const [layoutSettings, setLayoutSettings] = useState<any>({});
  const [brands, setBrands] = useState<{ name: string; logo: string; url: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingBrand, setUploadingBrand] = useState(false);

  // Dynamically load Google Font for preview
  useEffect(() => {
    if (form.font_family) {
      const font = form.font_family.replace(/ /g, '+');
      const linkId = 'design-google-font';
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      link.href = `https://fonts.googleapis.com/css2?family=${font}:wght@300;400;500;600;700&display=swap`;
    }
  }, [form.font_family]);

  useEffect(() => {
    if (store) {
      setForm({
        primary_color: store.primary_color || '#635BFF',
        secondary_color: store.secondary_color || '#14213D',
        accent_color: store.accent_color || '#0F9F9A',
        text_color: store.text_color || '#14213D',
        background_color: store.background_color || '#FFFFFF',
        font_family: store.font_family || 'Inter',
        layout: store.layout || 'modern',
      });
      setLayoutSettings(store.layout_settings || {});
      setBrands(store.layout_settings?.brands || []);
    }
  }, [store]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/stores/${store.id}`, form);
      await api.put(`/stores/${store.id}/layout`, { ...layoutSettings, brands });
      toast.success(dt('design_saved', lang));
      reloadStore();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleBrandLogoUpload = async (index: number, file: File) => {
    setUploadingBrand(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post(`/stores/${store.id}/brand-logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updated = [...brands];
      updated[index] = { ...updated[index], logo: res.data.path };
      setBrands(updated);
    } catch {
      toast.error(dt('error_saving', lang));
    } finally {
      setUploadingBrand(false);
    }
  };

  const addBrand = () => {
    setBrands([...brands, { name: '', logo: '', url: '' }]);
  };

  const removeBrand = (index: number) => {
    setBrands(brands.filter((_, i) => i !== index));
  };

  const updateBrand = (index: number, field: string, value: string) => {
    const updated = [...brands];
    updated[index] = { ...updated[index], [field]: value };
    setBrands(updated);
  };

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  if (!store) return null;

  const layouts = layoutIds.map((id) => ({
    id,
    name: dt(id, lang),
    desc: dt(`${id}_desc`, lang),
  }));

  return (
    <div>
      <div className="page-header">
        <h1>{dt('design_layout', lang)}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/dashboard/theme-editor" className="btn btn-white" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiEye size={16} /> {t('Live Editor', 'محرر مباشر')}
          </Link>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? dt('saving', lang) : dt('save_changes', lang)}
          </button>
        </div>
      </div>

      {/* Layout Selection */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h2>{dt('store_layout', lang)}</h2></div>
        <div className="design-layout-grid">
          {layouts.map((l) => (
            <div
              key={l.id}
              onClick={() => setForm({ ...form, layout: l.id })}
              style={{
                padding: 20,
                borderRadius: 'var(--radius-lg)',
                border: `2px solid ${form.layout === l.id ? 'var(--primary)' : 'var(--gray-100)'}`,
                cursor: 'pointer',
                transition: 'var(--transition)',
                position: 'relative',
                background: form.layout === l.id ? 'rgba(230, 57, 70, 0.03)' : 'white',
              }}
            >
              {form.layout === l.id && (
                <div style={{
                  position: 'absolute', top: 10, insetInlineEnd: 10, width: 24, height: 24,
                  background: 'var(--primary)', borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14,
                }}>
                  <FiCheck />
                </div>
              )}
              <div style={{
                height: 100, background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                marginBottom: 12, padding: 8, overflow: 'hidden',
              }}>
                {l.id === 'advanced' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, height: '100%' }}>
                    <div style={{ height: 16, background: 'var(--gray-200)', borderRadius: 3 }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 4, flex: 1 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ height: 8, background: 'var(--primary)', borderRadius: 2, opacity: 0.4 }} />
                        <div style={{ height: 8, background: 'var(--gray-200)', borderRadius: 2 }} />
                        <div style={{ height: 8, background: 'var(--gray-200)', borderRadius: 2 }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
                        {[1,2,3,4,5,6].map(i => <div key={i} style={{ background: 'var(--gray-200)', borderRadius: 2 }} />)}
                      </div>
                    </div>
                  </div>
                ) : l.id === 'food' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, height: '100%' }}>
                    <div style={{ background: 'var(--primary)', borderRadius: 3, opacity: 0.3 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ height: 16, background: 'var(--gray-200)', borderRadius: 3 }} />
                      <div style={{ height: 10, background: 'var(--gray-200)', borderRadius: 3, width: '60%' }} />
                      <div style={{ flex: 1, background: 'var(--gray-200)', borderRadius: 3 }} />
                      <div style={{ flex: 1, background: 'var(--gray-200)', borderRadius: 3 }} />
                    </div>
                  </div>
                ) : (<>
                  <div style={{
                    height: l.id === 'elegant' ? 30 : 20,
                    background: l.id === 'modern' ? 'var(--primary)' : l.id === 'classic' ? 'var(--secondary)' : l.id === 'minimal' ? 'transparent' : '#1a1a2e',
                    border: l.id === 'minimal' ? '1px solid var(--gray-200)' : 'none',
                    borderRadius: 4, marginBottom: 6,
                  }} />
                  <div style={{ display: 'grid', gridTemplateColumns: l.id === 'classic' ? '30px 1fr' : 'repeat(3, 1fr)', gap: 4 }}>
                    {l.id === 'classic' && <div style={{ background: 'var(--gray-200)', borderRadius: 3 }} />}
                    <div style={{ height: 20, background: 'var(--gray-200)', borderRadius: 3 }} />
                    {l.id !== 'classic' && <>
                      <div style={{ height: 20, background: 'var(--gray-200)', borderRadius: 3 }} />
                      <div style={{ height: 20, background: 'var(--gray-200)', borderRadius: 3 }} />
                    </>}
                  </div>
                </>)}
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: 'var(--secondary)', marginBottom: 4 }}>{l.name}</h4>
              <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>{l.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="design-grid">
        {/* Colors */}
        <div className="card">
          <div className="card-header"><h2>{dt('colors', lang)}</h2></div>
          {[
            { key: 'primary_color', label: dt('primary_color', lang) },
            { key: 'secondary_color', label: dt('secondary_color', lang) },
            { key: 'accent_color', label: dt('accent_color', lang) },
            { key: 'text_color', label: dt('text_color', lang) },
            { key: 'background_color', label: dt('background_color', lang) },
          ].map(({ key, label }) => (
            <div className="form-group" key={key}>
              <label className="form-label">{label}</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input
                  type="color"
                  value={form[key] || '#000000'}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={{ width: 50, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                />
                <input
                  className="form-input"
                  value={form[key] || ''}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Typography & Sections */}
        <div>
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header"><h2>{dt('typography', lang)}</h2></div>
            <div className="form-group">
              <label className="form-label">{dt('font_family', lang)}</label>
              <select className="form-input" value={form.font_family} onChange={(e) => setForm({ ...form, font_family: e.target.value })}>
                {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div style={{ padding: 20, background: 'var(--gray-50)', borderRadius: 'var(--radius)', fontFamily: form.font_family }}>
              <h3 style={{ marginBottom: 8 }}>{dt('preview_text', lang)}</h3>
              <p style={{ color: 'var(--gray-600)' }}>{dt('preview_sentence', lang)}</p>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header"><h2>{t('Logo Size', 'حجم الشعار')}</h2></div>
            <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
              {t('Set the logo dimensions in the store header. Leave empty for default size.', 'حدد أبعاد الشعار في رأس المتجر. اترك فارغاً للحجم الافتراضي.')}
            </p>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0, flex: 1 }}>
                <label className="form-label">{t('Width (px)', 'العرض (بكسل)')}</label>
                <input type="number" className="form-input" min="20" max="400" placeholder={t('Auto', 'تلقائي')}
                  value={layoutSettings.logo_width || ''}
                  onChange={(e) => setLayoutSettings({ ...layoutSettings, logo_width: e.target.value ? parseInt(e.target.value) : null })} />
              </div>
              <div className="form-group" style={{ margin: 0, flex: 1 }}>
                <label className="form-label">{t('Height (px)', 'الارتفاع (بكسل)')}</label>
                <input type="number" className="form-input" min="20" max="200" placeholder={t('Auto', 'تلقائي')}
                  value={layoutSettings.logo_height || ''}
                  onChange={(e) => setLayoutSettings({ ...layoutSettings, logo_height: e.target.value ? parseInt(e.target.value) : null })} />
              </div>
            </div>
            {store?.logo && (
              <div style={{ padding: 16, background: 'var(--gray-50)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={`${STORAGE_URL}/${store.logo}`}
                  alt="Logo preview"
                  style={{
                    width: layoutSettings.logo_width ? `${layoutSettings.logo_width}px` : 'auto',
                    height: layoutSettings.logo_height ? `${layoutSettings.logo_height}px` : '50px',
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header"><h2>{dt('section_visibility', lang)}</h2></div>
            {[
              { key: 'show_header_banner', label: dt('header_banner', lang) },
              { key: 'show_categories_bar', label: dt('categories_bar', lang) },
              { key: 'show_search', label: dt('search_bar', lang) },
              { key: 'show_featured', label: dt('featured_products', lang) },
              { key: 'show_store_info', label: dt('store_info_section', lang) },
              { key: 'show_social_links', label: dt('social_links', lang) },
              { key: 'show_delivery_info', label: dt('delivery_info', lang) },
              { key: 'show_working_hours', label: dt('working_hours', lang) },
              { key: 'sticky_nav', label: dt('sticky_nav', lang), defaultOff: true },
              { key: 'sticky_filters', label: dt('sticky_filters', lang), defaultOff: true },
              { key: 'infinite_scroll', label: dt('infinite_scroll', lang), defaultOff: true },
            ].map(({ key, label, defaultOff }: any) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', cursor: 'pointer', borderBottom: '1px solid var(--gray-50)' }}>
                <input
                  type="checkbox"
                  checked={defaultOff ? (layoutSettings[key] ?? false) : (layoutSettings[key] ?? true)}
                  onChange={(e) => setLayoutSettings({ ...layoutSettings, [key]: e.target.checked })}
                />
                <span style={{ fontSize: 14 }}>{label}</span>
              </label>
            ))}
          </div>

          {/* Font Sizes */}
          <div className="card">
            <div className="card-header"><h2>{dt('font_sizes', lang)}</h2></div>
            {[
              { key: 'font_size_title', label: dt('font_size_title', lang), defaultVal: 24, min: 14, max: 60 },
              { key: 'font_size_menu', label: dt('font_size_menu', lang), defaultVal: 14, min: 10, max: 30 },
              { key: 'font_size_product_name', label: dt('font_size_product_name', lang), defaultVal: 16, min: 10, max: 40 },
              { key: 'font_size_product_price', label: dt('font_size_product_price', lang), defaultVal: 16, min: 10, max: 40 },
              { key: 'font_size_body', label: dt('font_size_body', lang), defaultVal: 14, min: 10, max: 30 },
            ].map(({ key, label, defaultVal, min, max }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--gray-50)' }}>
                <span style={{ fontSize: 14, flex: 1 }}>{label}</span>
                <input
                  type="range"
                  min={min} max={max} step={1}
                  value={layoutSettings[key] || defaultVal}
                  onChange={(e) => setLayoutSettings({ ...layoutSettings, [key]: Number(e.target.value) })}
                  style={{ width: 120 }}
                />
                <span style={{ fontSize: 13, color: 'var(--gray-400)', minWidth: 35, textAlign: 'center' }}>{layoutSettings[key] || defaultVal}px</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Favicon */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><h2>{t('Favicon', 'أيقونة المتصفح')}</h2></div>
        <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 12 }}>
          {t('Upload a custom favicon for your store. If not set, your logo will be used.', 'ارفع أيقونة مخصصة لمتجرك. إذا لم تُحدد، سيتم استخدام الشعار.')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 8, border: '2px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'var(--gray-50)' }}>
            {store.favicon ? (
              <img src={`${STORAGE_URL}/${store.favicon}`} alt="Favicon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : store.logo ? (
              <img src={`${STORAGE_URL}/${store.logo}`} alt="Logo as favicon" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.5 }} />
            ) : (
              <span style={{ fontSize: 20, color: 'var(--gray-300)' }}>🌐</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <label className="btn btn-sm btn-white" style={{ cursor: 'pointer' }}>
              <FiUpload size={14} style={{ marginInlineEnd: 4 }} /> {t('Upload', 'رفع')}
              <input type="file" accept="image/*,.ico" style={{ display: 'none' }} onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('favicon', file);
                try {
                  await api.post(`/stores/${store.id}/favicon`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  toast.success(t('Favicon uploaded', 'تم رفع الأيقونة'));
                  reloadStore();
                } catch (err: any) {
                  toast.error(err.response?.data?.message || 'Upload failed');
                }
              }} />
            </label>
            {store.favicon && (
              <button className="btn btn-sm btn-white" style={{ color: 'var(--danger)' }} onClick={async () => {
                try {
                  await api.delete(`/stores/${store.id}/favicon`);
                  toast.success(t('Favicon removed', 'تم إزالة الأيقونة'));
                  reloadStore();
                } catch { toast.error('Error'); }
              }}>
                {t('Remove', 'إزالة')}
              </button>
            )}
          </div>
        </div>
        {!store.favicon && store.logo && (
          <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 8 }}>
            {t('Currently using your store logo as favicon', 'حالياً يتم استخدام شعار متجرك كأيقونة')}
          </p>
        )}
      </div>

      {/* Brands Section */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t('Brands', 'العلامات التجارية')}</h2>
          <button className="btn btn-primary btn-sm" onClick={addBrand}>
            <FiPlus /> {t('Add Brand', 'إضافة علامة')}
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
          {t('Brand logos will be displayed at the bottom of your storefront.', 'ستظهر شعارات العلامات التجارية في أسفل واجهة متجرك.')}
        </p>

        {brands.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--gray-400)' }}>
            {t('No brands added yet.', 'لم تتم إضافة علامات تجارية بعد.')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {brands.map((brand, i) => (
              <div key={i} style={{
                display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
                padding: 12, border: '1px solid var(--gray-100)', borderRadius: 6,
              }}>
                {/* Logo preview / upload */}
                <label style={{
                  width: 60, height: 60, borderRadius: 6, border: '2px dashed var(--gray-200)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  overflow: 'hidden', flexShrink: 0, background: 'var(--gray-50)',
                }}>
                  {brand.logo ? (
                    <img src={`${STORAGE_URL}/${brand.logo}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <FiUpload size={18} color="var(--gray-400)" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files?.[0] && handleBrandLogoUpload(i, e.target.files[0])}
                  />
                </label>
                {/* Name */}
                <input
                  className="form-input"
                  placeholder={t('Brand name', 'اسم العلامة')}
                  value={brand.name}
                  onChange={(e) => updateBrand(i, 'name', e.target.value)}
                  style={{ flex: 1, minWidth: 120 }}
                />
                {/* URL */}
                <input
                  className="form-input"
                  placeholder={t('Website URL (optional)', 'رابط الموقع (اختياري)')}
                  value={brand.url}
                  onChange={(e) => updateBrand(i, 'url', e.target.value)}
                  style={{ flex: 1, minWidth: 120 }}
                />
                {/* Delete */}
                <button
                  className="btn btn-sm btn-white"
                  style={{ color: 'var(--danger)', padding: '8px 10px' }}
                  onClick={() => removeBrand(i)}
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
